// =============================================================================
// Origin UI — src/services/assetApi.ts
// API calls for the IT Asset Management page and reports.
// =============================================================================

import { api } from '@/services/apiService';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// ─── Types ───

export interface AssetListItem {
  assetId: string;
  employeeId: string | null;
  employeeName: string | null;
  employeeNumber: string | null;
  departmentName: string | null;
  officeName: string | null;
  assetType: string;
  makeModel: string | null;
  serialNumber: string | null;
  assetTag: string | null;
  status: string;
  condition: string | null;
  assignedDate: string | null;
  returnedDate: string | null;
  purchaseDate: string | null;
  warrantyExpiryDate: string | null;
  returnDueDate: string | null;
  notes: string | null;
}

export interface AssetPagedResult {
  items: AssetListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface AssetHistoryItem {
  assetHistoryId: string;
  fromEmployeeId: string | null;
  fromEmployeeName: string | null;
  fromEmployeeNumber: string | null;
  conditionFromEmployee: string | null;
  toEmployeeId: string | null;
  toEmployeeName: string | null;
  toEmployeeNumber: string | null;
  conditionToEmployee: string | null;
  warrantyExpiryDateAtChange: string | null;
  changedDate: string;
  changedByName: string;
}

export interface AssetAttachmentItem {
  assetAttachmentId: string;
  fileName: string;
  blobPath: string;
  fileSize: number;
  uploadedDate: string;
  uploadedByName: string;
}

export interface AssetDetail {
  assetId: string;
  employeeId: string | null;
  employeeName: string | null;
  employeeNumber: string | null;
  assetType: string;
  makeModel: string | null;
  serialNumber: string | null;
  assetTag: string | null;
  status: string;
  condition: string | null;
  assignedDate: string | null;
  returnedDate: string | null;
  purchaseDate: string | null;
  warrantyExpiryDate: string | null;
  returnDueDate: string | null;
  notes: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedBy: string | null;
  updatedAt: string | null;
  history: AssetHistoryItem[];
  attachments: AssetAttachmentItem[];
}

export interface CreateAssetDto {
  employeeId?: string | null;
  assetType: string;
  makeModel?: string;
  serialNumber?: string;
  assetTag?: string;
  status: string;
  condition?: string;
  assignedDate?: string | null;
  purchaseDate?: string | null;
  warrantyExpiryDate?: string | null;
  notes?: string;
}

export interface UpdateAssetDto {
  employeeId?: string | null;
  assetType: string;
  makeModel?: string;
  serialNumber?: string;
  assetTag?: string;
  status: string;
  condition?: string;
  assignedDate?: string | null;
  returnedDate?: string | null;
  purchaseDate?: string | null;
  warrantyExpiryDate?: string | null;
  returnDueDate?: string | null;
  notes?: string;
}

export interface AssetsByOffice {
  officeName: string;
  total: number;
  assigned: number;
  pendingReturn: number;
  returned: number;
  lost: number;
  disposed: number;
}

export interface AssetsByType {
  assetType: string;
  total: number;
  assigned: number;
  pendingReturn: number;
  returned: number;
  lost: number;
  disposed: number;
  averageAgeDays: number;
}

export interface WarrantyExpiring {
  assetId: string;
  employeeName: string;
  employeeNumber: string;
  officeName: string;
  assetType: string;
  makeModel: string | null;
  assetTag: string | null;
  warrantyExpiryDate: string;
  daysRemaining: number;
}

export interface EmployeeSearchItem {
  employeeId: string;
  employeeNumber: string;
  firstName: string;
  lastName: string;
  preferredName: string | null;
  departmentName: string;
  officeName: string;
}

// ─── Company-wide asset API (ITOps + HRAdmin) ───

export const assetApi = {
  // Paged list with filters
  getAll: (params: {
    search?: string; office?: string; assetType?: string;
    status?: string; condition?: string; page?: number; pageSize?: number;
    sortBy?: string; sortDir?: string;
  }) => {
    const qs = new URLSearchParams();
    if (params.search) qs.set('search', params.search);
    if (params.office) qs.set('office', params.office);
    if (params.assetType) qs.set('assetType', params.assetType);
    if (params.status) qs.set('status', params.status);
    if (params.condition) qs.set('condition', params.condition);
    if (params.sortBy) qs.set('sortBy', params.sortBy);
    if (params.sortDir) qs.set('sortDir', params.sortDir);
    qs.set('page', String(params.page || 1));
    qs.set('pageSize', String(params.pageSize || 50));
    return api.get<AssetPagedResult>(`/api/assets?${qs.toString()}`);
  },

  // Asset detail (includes history + attachments)
  getDetail: (assetId: string) =>
    api.get<AssetDetail>(`/api/assets/${assetId}`),

  // Create asset (employeeId is optional — omit for unassigned)
  createAsset: (dto: CreateAssetDto) =>
    api.post<AssetDetail>('/api/assets', dto),

  // Update asset (handles reassignment + history automatically)
  updateAsset: (assetId: string, dto: UpdateAssetDto) =>
    api.put<AssetDetail>(`/api/assets/${assetId}`, dto),

  // ─── Attachments ───

  uploadAttachment: async (assetId: string, file: File) => {
    const token = await api.getToken();
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_BASE_URL}/api/assets/${assetId}/attachments`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Upload failed' }));
      throw new Error(err.message || 'Upload failed');
    }
    return response.json() as Promise<AssetAttachmentItem>;
  },

  downloadAttachment: async (assetId: string, attachmentId: string, fileName: string) => {
    const token = await api.getToken();
    const response = await fetch(`${API_BASE_URL}/api/assets/${assetId}/attachments/${attachmentId}/download`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Download failed');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  },

  deleteAttachment: (assetId: string, attachmentId: string) =>
    api.delete(`/api/assets/${assetId}/attachments/${attachmentId}`),

  // ─── Reports ───

  reportByOffice: () => api.get<AssetsByOffice[]>('/api/assets/reports/by-office'),
  reportByType: () => api.get<AssetsByType[]>('/api/assets/reports/by-type'),
  reportWarrantyExpiring: (days = 90) => api.get<WarrantyExpiring[]>(`/api/assets/reports/warranty-expiring?withinDays=${days}`),

  // CSV export — returns blob URL
  exportCsv: async (params: { office?: string; assetType?: string; status?: string; condition?: string }) => {
    const qs = new URLSearchParams();
    if (params.office) qs.set('office', params.office);
    if (params.assetType) qs.set('assetType', params.assetType);
    if (params.status) qs.set('status', params.status);
    if (params.condition) qs.set('condition', params.condition);
    const token = await api.getToken();
    const response = await fetch(`${API_BASE_URL}/api/assets/csv?${qs.toString()}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('CSV export failed');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `IT_Assets_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  },

  // Pending return count (for home screen)
  getPendingReturnCount: () => api.get<number>('/api/assets/pending-return-count'),

  // Employee search (for add/reassign asset form)
  searchEmployees: (query: string) =>
    api.get<EmployeeSearchItem[]>(`/api/employees?status=Active&search=${encodeURIComponent(query)}`),
};
